import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import React from "react";
import { Navigate, useSearchParams } from "react-router";
import { useLayoutContext, useIsInRole } from "@ntg/component-library/contexts";
import type { IODataFilterRow, IODataFormState } from "../declarations/ODataExplorer";
import CompanyPicker from "./CompanyPicker";
import EntityPicker from "./EntityPicker";
import FilterBuilder from "./FilterBuilder";
import OrderByControl from "./OrderByControl";
import PaginationBar from "./PaginationBar";
import ResultTable from "./ResultTable";
import SelectChips from "./SelectChips";
import TopControl from "./TopControl";
import { useODataMetadata, useRefreshODataMetadata } from "./hooks/useODataMetadata";
import { useODataQuery } from "./hooks/useODataQuery";
import { defaultFormState, formStateToParams, paramsToFormState } from "./utilities/urlState";

interface IServerErrorBody {
    message?: string;
    innerMessage?: string;
    code?: string;
    d365Status?: number;
    rawBody?: string;
}

function extractErrorBody(error: unknown): IServerErrorBody {
    type AxiosLike = { response?: { data?: IServerErrorBody | string } };
    const data = (error as AxiosLike).response?.data;
    if (typeof data === "object" && data !== null) return data;
    if (typeof data === "string") return { message: data };
    return { message: (error as Error)?.message ?? "Unknown error" };
}

// The OData Explorer is restricted to Global Admins. This guard enforces the
// restriction on the page itself, so direct navigation to /odata-explorer (and
// the brief role-loading window) is handled even though the nav item is also
// hidden from unauthorized users.
export default function ODataExplorerPage() {
    const { IsInRole: isGlobalAdmin, IsLoading } = useIsInRole("Global Admin");

    if (IsLoading || isGlobalAdmin === undefined) {
        return (
            <Container maxWidth="xl" sx={{ py: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "center" }}><CircularProgress /></Box>
            </Container>
        );
    }

    if (!isGlobalAdmin) {
        return <Navigate replace to="/" />;
    }

    return <ODataExplorerContent />;
}

function ODataExplorerContent() {
    const { SetPrimaryTitle, SetSecondaryHeader } = useLayoutContext();
    const [searchParams, setSearchParams] = useSearchParams();
    const [state, setState] = React.useState<IODataFormState>(() => paramsToFormState(searchParams));
    const metadataQuery = useODataMetadata();
    const refreshMutation = useRefreshODataMetadata();
    const queryResult = useODataQuery(state);

    const writeUrlTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    React.useEffect(() => {
        if (writeUrlTimeoutRef.current) {
            clearTimeout(writeUrlTimeoutRef.current);
        }
        writeUrlTimeoutRef.current = setTimeout(() => {
            setSearchParams(formStateToParams(state), { replace: true });
            writeUrlTimeoutRef.current = null;
        }, 300);
        return () => {
            if (writeUrlTimeoutRef.current) {
                clearTimeout(writeUrlTimeoutRef.current);
            }
        };
    }, [state, setSearchParams]);

    React.useEffect(() => {
        const parsed = paramsToFormState(searchParams);
        setState((current) => {
            // Preserve runId from current state — runId never appears in URL.
            const next = { ...parsed, runId: current.runId };
            // If the URL-derived state matches what we just wrote, do nothing.
            if (
                current.entity === next.entity
                && current.top === next.top
                && current.skip === next.skip
                && current.orderByProperty === next.orderByProperty
                && current.orderByDescending === next.orderByDescending
                && current.select.join(",") === next.select.join(",")
                && current.companies.join(",") === next.companies.join(",")
                && JSON.stringify(current.filters) === JSON.stringify(next.filters)
            ) {
                return current;
            }
            return next;
        });
    }, [searchParams]);

    const catalog = metadataQuery.data;
    const entity = catalog && state.entity ? catalog.entities[state.entity] : undefined;

    const filledFilters = state.filters.filter(
        (f) =>
            f.property !== "" &&
            (f.operator === "is null" || f.operator === "is not null" || f.value !== ""),
    );
    const canRun = catalog != null && entity != null && filledFilters.length > 0;

    const lastRunSnapshotRef = React.useRef<{
        runId: number;
        entity: string;
        select: string[];
        filters: IODataFilterRow[];
        orderByProperty: string;
        orderByDescending: boolean;
        top: number;
        companies: string[];
    } | null>(null);

    const onRun = () => setState((s) => {
        const nextRunId = s.runId + 1;
        lastRunSnapshotRef.current = {
            runId: nextRunId,
            entity: s.entity,
            select: s.select,
            filters: s.filters,
            orderByProperty: s.orderByProperty,
            orderByDescending: s.orderByDescending,
            top: s.top,
            companies: s.companies,
        };
        return { ...s, runId: nextRunId, skip: 0 };
    });
    const onSkipChange = (skip: number) => setState((s) => {
        const nextRunId = s.runId + 1;
        lastRunSnapshotRef.current = {
            runId: nextRunId,
            entity: s.entity,
            select: s.select,
            filters: s.filters,
            orderByProperty: s.orderByProperty,
            orderByDescending: s.orderByDescending,
            top: s.top,
            companies: s.companies,
        };
        return { ...s, runId: nextRunId, skip };
    });

    const didAutoRunRef = React.useRef(false);
    React.useEffect(() => {
        if (didAutoRunRef.current) return;
        if (!catalog || !state.entity) return;
        const hasFilledFilter = state.filters.some(
            (f) => f.property !== "" && (f.operator === "is null" || f.operator === "is not null" || f.value !== ""),
        );
        if (!hasFilledFilter) return;
        didAutoRunRef.current = true;
        setState((s) => {
            const next = { ...s, runId: s.runId + 1 };
            lastRunSnapshotRef.current = {
                runId: next.runId,
                entity: next.entity,
                select: next.select,
                filters: next.filters,
                orderByProperty: next.orderByProperty,
                orderByDescending: next.orderByDescending,
                top: next.top,
                companies: next.companies,
            };
            return next;
        });
    }, [catalog, state.entity, state.filters]);

    const isDirty = React.useMemo(() => {
        const snap = lastRunSnapshotRef.current;
        if (!snap) return state.runId === 0;
        return (
            state.entity !== snap.entity
            || state.top !== snap.top
            || state.orderByProperty !== snap.orderByProperty
            || state.orderByDescending !== snap.orderByDescending
            || state.select.join(",") !== snap.select.join(",")
            || state.companies.join(",") !== snap.companies.join(",")
            || JSON.stringify(state.filters) !== JSON.stringify(snap.filters)
        );
    }, [state]);

    React.useEffect(() => {
        SetPrimaryTitle("OData Explorer");
        SetSecondaryHeader(null);
        return () => {
            SetPrimaryTitle("");
            SetSecondaryHeader(null);
        };
    }, [SetPrimaryTitle, SetSecondaryHeader]);

    if (metadataQuery.isLoading) {
        return (
            <Container maxWidth="xl" sx={{ py: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "center" }}><CircularProgress /></Box>
            </Container>
        );
    }
    if (metadataQuery.isError || !metadataQuery.data) {
        return (
            <Container maxWidth="xl" sx={{ py: 3 }}>
                <Stack spacing={2}>
                    <Alert severity="error">Failed to load D365 schema. {String(metadataQuery.error)}</Alert>
                    <Button onClick={() => metadataQuery.refetch()} variant="contained">Retry</Button>
                </Stack>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Stack spacing={2}>
            <Stack direction="row" spacing={2} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                <Tooltip title="Refresh schema">
                    <IconButton onClick={() => refreshMutation.mutate()} size="small">
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <EntityPicker
                    catalog={catalog!}
                    onChange={(name) => setState({ ...defaultFormState(), entity: name })}
                    value={state.entity}
                />
                <CompanyPicker
                    onChange={(c) => setState((s) => ({ ...s, companies: c }))}
                    value={state.companies}
                />
                <Button
                    color={isDirty && canRun ? "warning" : "primary"}
                    disabled={!canRun}
                    onClick={onRun}
                    startIcon={<PlayArrowIcon />}
                    sx={isDirty && canRun ? {
                        animation: "odataRunPulse 1.4s ease-in-out infinite",
                        "@keyframes odataRunPulse": {
                            "0%, 100%": { boxShadow: "0 0 0 0 rgba(255, 152, 0, 0.0)" },
                            "50%": { boxShadow: "0 0 0 6px rgba(255, 152, 0, 0.3)" },
                        },
                    } : undefined}
                    variant="contained"
                >
                    Run
                </Button>
            </Stack>
            {entity && (
                <Stack spacing={1}>
                    <FilterBuilder
                        catalog={catalog!}
                        entity={entity}
                        onChange={(rows) => setState((s) => ({ ...s, filters: rows }))}
                        rows={state.filters}
                    />
                    {filledFilters.length === 0 && (
                        <Alert severity="info">
                            Add at least one filter row before running. Querying without a filter can return very large result sets.
                        </Alert>
                    )}
                    <Accordion sx={{ mt: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="body2">Advanced</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Stack direction="row" spacing={2} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                                <SelectChips entity={entity} onChange={(sel) => setState((s) => ({ ...s, select: sel }))} value={state.select} />
                                <OrderByControl
                                    descending={state.orderByDescending}
                                    entity={entity}
                                    onChange={(prop, desc) => setState((s) => ({ ...s, orderByProperty: prop, orderByDescending: desc }))}
                                    property={state.orderByProperty}
                                />
                                <TopControl onChange={(top) => setState((s) => ({ ...s, top }))} value={state.top} />
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                </Stack>
            )}

            {queryResult.isLoading && <CircularProgress />}
            {queryResult.isError && (() => {
                const body = extractErrorBody(queryResult.error);
                return (
                    <Alert severity="error" sx={{ "& .MuiAlert-message": { width: "100%" } }}>
                        <Stack spacing={0.5}>
                            {body.d365Status != null && (
                                <Typography variant="caption">D365 status: {body.d365Status}{body.code ? ` · ${body.code}` : ""}</Typography>
                            )}
                            {body.message && <Typography variant="body2">{body.message}</Typography>}
                            {body.innerMessage && (
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{body.innerMessage}</Typography>
                            )}
                            {body.rawBody && (
                                <details>
                                    <summary style={{ cursor: "pointer" }}>Raw response</summary>
                                    <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "0.75rem", margin: 0 }}>
                                        {body.rawBody}
                                    </pre>
                                </details>
                            )}
                        </Stack>
                    </Alert>
                );
            })()}
            {queryResult.data && (
                <Stack spacing={1}>
                    {queryResult.data.clamped && (
                        <Alert severity="warning">$top was clamped to 500. Reduce or paginate.</Alert>
                    )}
                    <ResultTable columns={state.select} rows={queryResult.data.value} />
                    <PaginationBar
                        onSkipChange={onSkipChange}
                        skip={state.skip}
                        top={state.top}
                        totalCount={queryResult.data.totalCount}
                    />
                </Stack>
            )}
            </Stack>
        </Container>
    );
}
